import { addDays, differenceInMinutes, startOfDay } from 'date-fns';
import { CalendarItem, IItemPosition } from '../types';
import { getDayKey } from './dateRange';

/** Guard against a corrupt/huge range flooding the grid with day buckets. */
const MAX_SPAN_DAYS = 366;

/** Default duration (minutes) for items that have a start but no end. */
export const DEFAULT_ITEM_MINUTES = 30;
/** Minutes in a day — the height of a single day column in the time grid. */
export const MINUTES_IN_DAY = 24 * 60;

/** An item paired with its computed time-grid geometry. */
export interface IPositionedItem {
	item: CalendarItem;
	position: IItemPosition;
}

/**
 * Bucket items by every local calendar day they span (start → end, inclusive), so a
 * multi-day item appears on each of its days. Single-day items land in exactly one bucket.
 * The returned map is keyed by {@link getDayKey}.
 */
export function groupItemsByDay(items: CalendarItem[]): Map<string, CalendarItem[]> {
	const byDay = new Map<string, CalendarItem[]>();
	const add = (key: string, item: CalendarItem) => {
		const existing = byDay.get(key);
		if (existing) {
			existing.push(item);
		} else {
			byDay.set(key, [item]);
		}
	};

	for (const item of items) {
		const firstDay = startOfDay(item.start);
		// Subtract 1ms so an item ending exactly at midnight doesn't leak into the next day.
		const endInstant = item.end && item.end > item.start ? new Date(item.end.getTime() - 1) : item.start;
		const lastDay = startOfDay(endInstant);

		let cursor = firstDay;
		for (let i = 0; cursor <= lastDay && i < MAX_SPAN_DAYS; i++) {
			add(getDayKey(cursor), item);
			cursor = addDays(cursor, 1);
		}
	}
	return byDay;
}

/** Clamp an item's start/end to the given day and express them as minute offsets. */
function getDayMinutes(item: CalendarItem, day: Date): { startMinutes: number; endMinutes: number } {
	const dayStart = startOfDay(day);
	const end = item.end ?? new Date(item.start.getTime() + DEFAULT_ITEM_MINUTES * 60_000);

	const rawStart = differenceInMinutes(item.start, dayStart);
	const rawEnd = differenceInMinutes(end, dayStart);

	const startMinutes = Math.max(0, Math.min(MINUTES_IN_DAY, rawStart));
	// Guarantee at least a sliver of height even for zero-length items.
	const endMinutes = Math.max(startMinutes + 1, Math.min(MINUTES_IN_DAY, rawEnd));
	return { startMinutes, endMinutes };
}

/**
 * Lay out one day's items into side-by-side columns so overlapping items don't
 * visually stack on top of each other. Items are grouped into clusters of mutual
 * overlap; within a cluster each item gets the left-most free column, and every item
 * in the cluster reports the cluster's column count so widths divide evenly.
 */
export function layoutDayItems(items: CalendarItem[], day: Date): IPositionedItem[] {
	const timed = items
		.map(item => ({ item, ...getDayMinutes(item, day) }))
		.sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);

	const results: IPositionedItem[] = [];
	let cluster: typeof timed = [];
	let clusterEnd = -1;

	const flushCluster = () => {
		if (cluster.length === 0) {
			return;
		}
		// Greedy column assignment: place each item in the first column whose last
		// item has already ended.
		const columnEnds: number[] = [];
		const assigned = cluster.map(entry => {
			let column = columnEnds.findIndex(end => end <= entry.startMinutes);
			if (column === -1) {
				column = columnEnds.length;
				columnEnds.push(entry.endMinutes);
			} else {
				columnEnds[column] = entry.endMinutes;
			}
			return { entry, column };
		});
		const columnCount = columnEnds.length;
		for (const { entry, column } of assigned) {
			results.push({
				item: entry.item,
				position: {
					startMinutes: entry.startMinutes,
					endMinutes: entry.endMinutes,
					column,
					columnCount,
				},
			});
		}
		cluster = [];
		clusterEnd = -1;
	};

	for (const entry of timed) {
		if (cluster.length > 0 && entry.startMinutes >= clusterEnd) {
			// No overlap with the running cluster — close it out first.
			flushCluster();
		}
		cluster.push(entry);
		clusterEnd = Math.max(clusterEnd, entry.endMinutes);
	}
	flushCluster();

	return results;
}

/** Earliest item start minute across a set of positions, for auto-scrolling the grid. */
export function earliestStartMinute(positioned: IPositionedItem[]): number | null {
	if (positioned.length === 0) {
		return null;
	}
	return positioned.reduce((min, p) => Math.min(min, p.position.startMinutes), MINUTES_IN_DAY);
}
